import { Component, Input, OnInit } from '@angular/core';
import { UntypedFormBuilder, Validators } from '@angular/forms';
import { Client } from '@classes/client';
import { License } from '@classes/license';
import { FormEditBaseComponent } from '@components/form-edit-base.component';
import { LicensesService } from '@services/licenses.service';
import { UiService } from '@services/ui.service';
import { validateHeaderName } from 'http';
import { Observable } from 'rxjs';

@Component({
  selector: 'app-license-edit',
  templateUrl: './license-edit.component.html',
  styleUrls: ['./license-edit.component.scss']
})
export class LicenseEditComponent extends FormEditBaseComponent implements OnInit {

  @Input() license: License = null;
  @Input() clients: Client[] = [];
  private _clientLicenses: License[] = [];

  public conflictWithActualLicenses: boolean = false;

  public override editForm = this.fb.group({
    client: [null, [Validators.required]],
    startDate: [null, [Validators.required]],
    endDate: [null, []],
    monthlyPrice: [null, [Validators.required, Validators.min(0)]],
    pricePerExtraOrder: [null, [Validators.required, Validators.min(0)]],
    maxOrdersPerMonth: [null, [Validators.required, Validators.min(0)]],
    comment: [null],
    conflict: [false, [Validators.required]]
  });

  constructor(
    protected uiSrv: UiService,
    protected fb: UntypedFormBuilder,
    private _licensesSrv: LicensesService,
  ) {
    super(uiSrv, fb);
  }

  public ngOnInit(): void {
    this._patchFormIfEditMode();
    if (!this._isCreateMode())
      this._updateClientLicenses(this.license.client);

    this._initializeClientChangeSubscription();
    this._initializeDatesChangeSubscription();
  }

  private _initializeClientChangeSubscription(): void {
    this.addSubscription(
      this.editForm.get('client').valueChanges.subscribe((client: Client) => {
        if (client === null || !(client instanceof Client)) {
          this._clientLicenses = [];
          return;
        }

        this._updateClientLicenses(client);
      })
    );
  }

  private _updateClientLicenses(client: Client): void {
    this._fetchClientLicenses$(client).subscribe((licenses: License[]) => {
      this._clientLicenses = licenses;
      this._removeCurrentLicenseFromClientLicenses();
      this._checkForConflictWithActualLicenses();
    });
  }

  private _removeCurrentLicenseFromClientLicenses(): void {
    if (this._isCreateMode()) return;
    this._clientLicenses = this._clientLicenses.filter(l => l.id !== this.license.id);
  }

  private _initializeDatesChangeSubscription(): void {
    this.addSubscription(
      this.editForm.get('startDate').valueChanges.subscribe(() => {
        this._checkForConflictWithActualLicenses();
      })
    );

    this.addSubscription(
      this.editForm.get('endDate').valueChanges.subscribe(() => {
        this._checkForConflictWithActualLicenses();
      })
    );
  }

  private _fetchClientLicenses$(client: Client): Observable<License[]> {
    return this._licensesSrv.fetchClientLicenses$(client);
  }

  private _checkForConflictWithActualLicenses(): void {
    this.conflictWithActualLicenses = this._clientLicenses.some(l => this._isConflictWithLicense(l));
    this.editForm.get('conflict').setValue(this.conflictWithActualLicenses ? null : false);
  }

  /**
   * Checks if the selected date range conflicts with the license date range.
   *
   * A conflict is defined as any overlap between the selected date range and the license date range.
   * If the selected start date is within the license date range, there is a conflict.
   * If the selected end date is within the license date range, there is a conflict.
   * If the selected date range encompasses the license date range, there is a conflict.
   *
   * @param {License} license - The license to check for conflicts.
   *
   * @returns {boolean} - Returns `true` if there is a conflict, `false` otherwise.
   */
  private _isConflictWithLicense(license: License): boolean {
    const selectedStartDate = new Date(this.editForm.get('startDate').value);

    const selectedEndDateValue = this.editForm.get('endDate').value;
    const selectedEndDate = selectedEndDateValue
      ? new Date(selectedEndDateValue)
      : null;

    const licenseStartDate = new Date(license.startDate);
    const licenseEndDate = new Date(license.endDate);

    if (!selectedStartDate) return false;

    return this._isStartDateInLicenseRange(selectedStartDate, licenseStartDate, licenseEndDate) ||
      this._isEndDateInLicenseRange(selectedEndDate, licenseStartDate, licenseEndDate) ||
      this._doesSelectedRangeEncompassLicense(selectedStartDate, selectedEndDate, licenseStartDate, licenseEndDate) ||
      this._isNoEndDateAndLicenseStartDateIsAfterSelectedStartDate(selectedStartDate, selectedEndDate, licenseStartDate);
  }

  private _isStartDateInLicenseRange(selectedStartDate: Date, licenseStartDate: Date, licenseEndDate: Date): boolean {
    return selectedStartDate >= licenseStartDate && (licenseEndDate ? selectedStartDate <= licenseEndDate : true);
  }

  private _isEndDateInLicenseRange(selectedEndDate: Date, licenseStartDate: Date, licenseEndDate: Date): boolean {
    return selectedEndDate >= licenseStartDate && (licenseEndDate ? selectedEndDate <= licenseEndDate : true);
  }

  private _doesSelectedRangeEncompassLicense(selectedStartDate: Date, selectedEndDate: Date, licenseStartDate: Date, licenseEndDate: Date): boolean {
    return selectedStartDate <= licenseStartDate && (licenseEndDate ? selectedEndDate >= licenseEndDate : true);
  }

  private _isNoEndDateAndLicenseStartDateIsAfterSelectedStartDate(selectedStartDate: Date, selectedEndDate: Date, licenseStartDate: Date): boolean {
    return !selectedEndDate && licenseStartDate > selectedStartDate;
  }

  public onEndForm(submit: boolean): void {
    if (submit) {
      this._submit();
    }
    else {
      this._cancel();
    }
  }

  private _submit(): void {
    const license = this._buildLicense();

    this._postOrPutLicense(license);
    this._editFinished();
  }

  private _buildLicense(): License {
    let license = this._newOrEditedLicense();

    Object.assign(license, this.editForm.value);

    return license;
  }

  private _newOrEditedLicense(): License {
    return this.license
      ? { ...this.license } as License
      : new License();
  }

  private _postOrPutLicense(license: License): void {
    if (this._isCreateMode()) {
      this._postLicense(license);
    }
    else {
      this._updateLicense(license);
    }
  }

  private _postLicense(license: License): void {
    this._licensesSrv.post(license);
  }

  private _updateLicense(license: License): void {
    this._licensesSrv.put(license);
  }

  private _cancel(): void {
    this._editFinished();
  }

  private _editFinished(): void {
    this.editFinished.next();
  }

  private _isCreateMode(): boolean {
    return this.license == null;
  }

  private _patchFormIfEditMode(): void {
    if (!this._isCreateMode()) {
      this.editForm.patchValue(this.license);
      this._patchClient();
    }
  }

  private _patchClient(): void {
    const client = this.clients.find(c => c.id === this.license.client.id);
    this.editForm.get('client').setValue(client);
  }
}